<script setup lang="ts">
import { computed } from 'vue';
import {
  useSectionsStore,
  defaultSections,
  sectionAnchorMap,
  sectionTrackLabelMap,
  type SectionName,
} from '@dh-io-owpi/spa/src/stores/sections';
import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';

const props = defineProps<{
  isDark: boolean;
}>();

const sectionsStore = useSectionsStore();
const { track } = useTrackingStore();

// Filtering out the undefined names & stage section from the list
const sectionsNames = computed(() => {
  return defaultSections.filter((name) => sectionsStore.visible[name] && sectionAnchorMap[name] && name !== 'stage');
});

const scrollToSection = (sectionId: SectionName) => {
  sectionsStore.scrollIntoView(sectionId);
};

const styles = computed(() => {
  const currentSection = sectionsNames.value.indexOf(sectionsStore.active);
  return {
    '--section-nbr': `${sectionsNames.value.length}`,
    '--current-section': `${currentSection}`,
  };
});

const trackSubnavigationItem = (name: SectionName) => {
  track({
    timeout: 2000,
    category: 'link',
    action: 'vertical-navigation',
    label: sectionTrackLabelMap[name],
  });
};
</script>

<template>
  <div
    class="owpi-sub-navigation"
    :class="{
      'owpi-sub-navigation--initial': sectionsStore.active === 'stage',
      'owpi-sub-navigation--dark': props.isDark,
    }"
    :style="styles"
  >
    <p class="wb-type-hint-strong owpi-sub-navigation__active-section-name">
      {{ $t(`${sectionsStore.active}.subline`) }}
    </p>
    <div class="owpi-sub-navigation__bar-wrapper">
      <div class="owpi-sub-navigation__bar"></div>
    </div>
    <wb-icon class="owpi-sub-navigation__icon" name="bds/chevron-down/24" />
    <aside class="owpi-sub-navigation__list">
      <div v-for="name in sectionsNames" :key="name">
        <a
          class="wb-type-button-secondary"
          :href="`#${sectionAnchorMap[name]}`"
          @click.prevent="
            scrollToSection(name);
            trackSubnavigationItem(name);
          "
          @auxclick="trackSubnavigationItem(name)"
        >
          {{ $t(`${name}.subline`) }}
        </a>
      </div>
    </aside>
  </div>
</template>

<style lang="scss">
@import '@dh-io-owpi/dynamic-stage/src/variables.sass';

$current-section: var(--current-section);
$section-nbr: var(--section-nbr);

$bar-height: 48px;
$bar-width: 3px;
$bar-offset: 34px;
$bar-element-gap: 24px;
$bar-wrapper-height: calc($section-nbr * $bar-height);

$initial-animation-duration: 6s;
$initial-animation-delay: 1s;
$transition-duration: 0.2s;

.owpi-sub-navigation {
  position: fixed;
  top: 50%;
  transform: translate(0, -50%);
  z-index: 100;
  transition:
    background-color $transition-duration $wb-fade,
    box-shadow $transition-duration $wb-fade,
    top calc($transition-duration * 2) $wb-fade;

  &__active-section-name {
    position: absolute;
    left: calc($bar-offset - 6px);
    bottom: calc(50% + $bar-wrapper-height / 2 + $bar-element-gap);
    transform: scale(-1);
    transition: opacity 1s $wb-fade;
    pointer-events: none;
    writing-mode: vertical-lr;
    text-transform: uppercase;
    text-wrap: nowrap;
    color: var(--wb-black);
    opacity: 1;
  }

  &__bar-wrapper {
    position: absolute;
    left: $bar-offset;
    height: $bar-wrapper-height;
  }

  &__bar {
    position: absolute;
    top: calc($current-section * $bar-height);
    transition:
      top $transition-duration $wb-fade,
      height $transition-duration $wb-fade;
    height: $bar-height;
    width: $bar-width;
    background-color: var(--wb-blue-45);
  }

  &__icon {
    display: none;
  }

  &__list {
    pointer-events: none;
    z-index: -1;
    transition: background-color $transition-duration $wb-fade;

    > div {
      border-left: $bar-width solid rgba(219, 219, 219, 0.5);
      margin-left: $bar-offset;
    }

    a {
      display: flex;
      align-items: center;
      overflow-x: hidden;
      padding-inline: $bar-offset;
      transition: width $transition-duration;
      width: 0;
      height: $bar-height;
      text-wrap: nowrap;
      text-decoration: none;
      color: var(--wb-grey-10);
      opacity: 0;

      &:hover {
        background-color: var(--wb-grey-90);
      }
    }
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.5);
    backdrop-filter: saturate(30%) blur(30px);
    border-radius: 0 5px 5px 0;
    box-shadow: var(--wb-shadow-level-1);
    .owpi-sub-navigation {
      &__list {
        pointer-events: all;

        a {
          opacity: 1;
          width: 100%;
        }
      }
      &__active-section-name {
        transition: opacity 0.1s $wb-fade;
        opacity: 0;
      }
    }
  }

  &--dark {
    .owpi-sub-navigation {
      &__active-section-name {
        color: var(--wb-white);
      }
      &__bar {
        background-color: var(--wb-red-45);
      }
      &__list > div {
        border-color: rgba(219, 219, 219, 0.3);
      }
    }
  }

  &--initial {
    top: calc(($desktop-heading-height + $desktop-stage-height) / 2);

    .owpi-sub-navigation {
      &__list:hover > div {
        border-color: var(--wb-white);
      }
      &__active-section-name {
        opacity: 0;
      }
      &__bar {
        top: 0;
        height: 0;
        animation: owpi-spa-navigation__initial-animation $initial-animation-duration infinite $initial-animation-delay;
        background-color: var(--wb-white);
      }
      &__icon {
        display: block;
        position: fixed;
        top: calc(50% + $bar-wrapper-height / 2);
        left: calc($bar-offset - 10.5px);
        z-index: 1;
        animation: owpi-spa-navigation__icon-animation $initial-animation-duration linear infinite;
        animation-delay: $initial-animation-delay;
        color: var(--wb-white);
        pointer-events: none;
        opacity: 0;

        svg {
          transform: scale(1.2);
        }
      }
    }

    @keyframes owpi-spa-navigation__initial-animation {
      0% {
        top: 0%;
        height: 0%;
      }
      25% {
        top: 0%;
        height: 100%;
      }
      50% {
        top: 100%;
        height: 0%;
      }
      100% {
        top: 100%;
        height: 0%;
      }
    }

    @keyframes owpi-spa-navigation__icon-animation {
      0% {
        opacity: 0;
        transform: translateY(0%);
      }
      45% {
        opacity: 0;
        transform: translateY(0%);
      }
      55% {
        opacity: 1;
      }
      70% {
        opacity: 0;
        transform: translateY(50%);
      }
    }
  }

  @include breakpoint-to(mq3) {
    display: none;
  }
}
</style>
